import { useEffect, useMemo, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import Peaks from 'peaks.js';
import Konva from 'konva';
import {
  Box, CircularProgress, IconButton, Slider, Typography,
  Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Tooltip, Button,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { resolveMediaSrc } from './index';

const ZOOM_LEVELS = [16, 32, 64, 128, 256, 512];

export const DEFAULT_SEGMENT_DURATION = 0.3;

export const SEGMENTATION_LABELS = [
  { value: 'SPEECH',  label: 'Speech',  color: '#6366f1' },
  { value: 'NOISE',   label: 'Noise',   color: '#ef4444' },
  { value: 'SILENCE', label: 'Silence', color: '#9ca3af' },
  { value: 'MUSIC',   label: 'Music',   color: '#10b981' },
  { value: 'OTHER',   label: 'Other',   color: '#f59e0b' },
];

export const ANNOTATION_LABELS = [
  { value: 'SPEECH',      label: 'Speech',      color: '#6366f1' },
  { value: 'NOISE',       label: 'Noise',       color: '#ef4444', isDefault: true },
  { value: 'SILENCE',     label: 'Silence',     color: '#9ca3af' },
  { value: 'MUSIC',       label: 'Music',       color: '#10b981' },
  { value: 'OVERLAPPING', label: 'Overlapping', color: '#f97316' },
  { value: 'OTHER',       label: 'Other',       color: '#f59e0b' },
];

const TAG_COLOR_POOL = [
  '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316',
  '#84cc16', '#06b6d4', '#f43f5e', '#a78bfa', '#fbbf24',
];

function makeGetTagColor() {
  const registry = new Map();
  let counter = 0;
  return function getTagColor(tag) {
    if (!tag) return null;
    if (!registry.has(tag)) {
      registry.set(tag, TAG_COLOR_POOL[counter % TAG_COLOR_POOL.length]);
      counter++;
    }
    return registry.get(tag);
  };
}

function buildLabelMap(labels) {
  return Object.fromEntries(labels.map((l) => [l.value, l]));
}

function r3(n) { return Math.round(n * 1000) / 1000; }

// Segments tagged [autoGeneratedTag] (any bracket style, case-insensitive)
// get a distinct row tint in the segment table.
function isAutoGenTag(tag) {
  return (tag ?? '').replace(/^[<[]|[>\]]$/g, '').trim().toLowerCase() === 'autogeneratedtag';
}

const HANDLE_W = 10;
const HANDLE_H = 54;
const MOBILE_RX = 18;
const MOBILE_RY = 38;

function createMarkerFactory(isMobile) {
  return function makeSegmentMarker({ editable, layer, startMarker, segmentOptions }) {
    let shape;
    const gripLines = [];
    const w = isMobile ? MOBILE_RX * 2 : HANDLE_W;
    const h = isMobile ? MOBILE_RY * 2 : HANDLE_H;
    const rectX = startMarker ? 0 : -w;
    const overlayOffset = segmentOptions?.overlayOffset ?? 25;

    function centeredY() {
      const lh = layer.getHeight();
      const overlayH = Math.max(0, lh - 2 * overlayOffset);
      return overlayOffset + (overlayH - h) / 2;
    }

    function gripY(i) {
      return centeredY() + h / 2 + (i - 1) * 8;
    }

    return {
      init(group) {
        shape = new Konva.Rect({
          x: rectX,
          y: centeredY(),
          width: w,
          height: h,
          fill: isMobile ? 'rgba(255,255,255,0.92)' : 'rgba(255,255,255,0.85)',
          stroke: isMobile ? '#2d5a8e' : 'rgba(255,255,255,0.5)',
          strokeWidth: isMobile ? 3 : 1,
          cornerRadius: isMobile ? 10 : 2,
          shadowColor: isMobile ? '#1e3a5f' : 'transparent',
          shadowBlur: isMobile ? 6 : 0,
          shadowOffsetX: isMobile ? 2 : 0,
          shadowOffsetY: isMobile ? 2 : 0,
          shadowOpacity: isMobile ? 0.4 : 0,
          visible: editable !== false,
        });
        group.add(shape);
        if (isMobile) {
          const cx = rectX + w / 2;
          const lineHalfW = w * 0.35;
          for (let i = 0; i < 3; i++) {
            const line = new Konva.Line({
              points: [cx - lineHalfW, gripY(i), cx + lineHalfW, gripY(i)],
              stroke: '#2d5a8e',
              strokeWidth: 1.5,
              lineCap: 'round',
              opacity: 0.65,
              visible: editable !== false,
            });
            group.add(line);
            gripLines.push(line);
          }
        }
      },
      fitToView() {
        shape?.y(centeredY());
        gripLines.forEach((line, i) => {
          const cx = rectX + w / 2;
          const lineHalfW = w * 0.35;
          const y = gripY(i);
          line.points([cx - lineHalfW, y, cx + lineHalfW, y]);
        });
      },
      update() {},
    };
  };
}

const SegmentationPlayer = forwardRef(function SegmentationPlayer({ audioFileURL, initialSegments = [], onSegmentsChange, labels = SEGMENTATION_LABELS, tagOptions, showLabels = false, readOnly = false, hideTable = false }, ref) {
  const LABEL_MAP = useMemo(() => buildLabelMap(labels), [labels]);
  const DEFAULT_LABEL = useMemo(
    () => (labels.find(l => l.isDefault) ?? labels[0])?.value ?? 'OTHER',
    [labels],
  );
  const audioRef = useRef(null);
  const audioCtxRef = useRef(null);
  const audioBufferRef = useRef(null);
  const sourceNodeRef = useRef(null);
  const segPlayRafRef = useRef(null);
  const zoomviewRef = useRef(null);
  const overviewRef = useRef(null);
  const peaksRef = useRef(null);
  const blobUrlRef = useRef(null);
  const colorIdxRef = useRef(0);
  const getTagColorRef = useRef(makeGetTagColor());
  const visibleWindowRef = useRef({ start: 0, end: 0 });
  const playingSegIdRef = useRef(null);
  const segPlayTimerRef = useRef(null);
  const anchorIdRef = useRef(null);

  const initialSegmentsLoadedRef = useRef(false);

  const [loaded, setLoaded] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState('');
  const [zoomSeconds, setZoomSeconds] = useState(1);
  const [imageSrc, setImageSrc] = useState(null);
  const [fallbackSrc, setFallbackSrc] = useState(null);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);

  const [segments, setSegments] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [rawValues, setRawValues] = useState({});
  const [playingSegId, setPlayingSegId] = useState(null);
  const [playbackRate, setPlaybackRate] = useState(1);

  function setPlayingSeg(id) {
    playingSegIdRef.current = id;
    setPlayingSegId(id);
  }

  function destroyPeaks() {
    cancelAnimationFrame(segPlayRafRef.current);
    clearTimeout(segPlayTimerRef.current);
    try { sourceNodeRef.current?.stop(); sourceNodeRef.current?.disconnect(); } catch (_) {}
    sourceNodeRef.current = null;
    audioBufferRef.current = null;
    audioCtxRef.current?.close();
    audioCtxRef.current = null;
    if (peaksRef.current) { peaksRef.current.destroy(); peaksRef.current = null; }
    if (blobUrlRef.current) { URL.revokeObjectURL(blobUrlRef.current); blobUrlRef.current = null; }
    colorIdxRef.current = 0;
    initialSegmentsLoadedRef.current = false;
    setLoaded(false); setPlaying(false); setCurrentTime(0); setDuration(0);
    setError(''); setZoomSeconds(1); setImageSrc(null); setFallbackSrc(null);
    setSegments([]); setSelectedIds(new Set()); setRawValues({}); setPlayingSeg(null);
    setPlaybackRate(1);
    anchorIdRef.current = null;
  }

  function syncSegmentsFromPeaks() {
    const p = peaksRef.current;
    if (!p) return;
    setSegments((prev) => {
      const peakById = Object.fromEntries(
        p.segments.getSegments().flatMap((s) => (s.id ? [[s.id, s]] : []))
      );
      const next = prev
        .flatMap((s) => {
          const ps = peakById[s.id];
          return ps ? [{ ...s, start: r3(ps.startTime), end: r3(ps.endTime) }] : [];
        })
        .sort((a, b) => a.start - b.start);
      onSegmentsChange?.(next);
      return next;
    });
  }

  function setEditableSegment(activeId) {
    if (readOnly) return;
    const p = peaksRef.current;
    if (!p) return;
    for (const seg of p.segments.getSegments()) {
      seg.update({ editable: !activeId || seg.id === activeId });
    }
    if (activeId) {
      const seg = p.segments.getSegment(activeId);
      if (seg) {
        const props = { id: seg.id, startTime: seg.startTime, endTime: seg.endTime, color: seg.color, borderColor: seg.borderColor, labelText: seg.labelText, editable: true, overlay: true, markers: true };
        p.segments.removeById(seg.id);
        p.segments.add(props);
      }
    }
  }

  function addSegmentRegion(start, end, label = DEFAULT_LABEL, tag = null, makeActive = false) {
    const p = peaksRef.current;
    if (!p) return;
    const tagColor = getTagColorRef.current(tag);
    const color = tagColor ?? (LABEL_MAP[label]?.color ?? LABEL_MAP[DEFAULT_LABEL].color);
    colorIdxRef.current++;
    const seg = p.segments.add({
      startTime: start, endTime: end, editable: !readOnly,
      color, borderColor: color, overlay: true, markers: true, labelText: tag || label,
    });
    if (!seg?.id) return;
    if (makeActive) setEditableSegment(seg.id);
    setSegments((prev) => {
      const next = [...prev, { id: seg.id, start: r3(seg.startTime), end: r3(seg.endTime), label, tag }].sort((a, b) => a.start - b.start);
      onSegmentsChange?.(next);
      return next;
    });
  }

  function updatePeaksBorders(p, ids) {
    if (!p) return;
    for (const seg of p.segments.getSegments()) {
      if (seg.id) seg.update({ borderColor: ids.has(seg.id) ? '#ffffff' : seg.color });
    }
  }

  function initPeaks(src) {
    const isMobile = navigator.maxTouchPoints > 0 || window.innerWidth <= 768;
    const audio = audioRef.current;
    const zoomEl = zoomviewRef.current;
    const overviewEl = overviewRef.current;
    if (!audio || !zoomEl || !overviewEl || !src) return;
    audio.src = src;

    Peaks.init(
      {
        mediaElement: audio,
        zoomview: {
          container: zoomEl,
          waveformColor: '#6366f1',
          playedWaveformColor: '#312e81',
          playheadColor: '#f59e0b',
          axisLabelColor: '#6b7280',
          axisGridlineColor: '#e5e7eb',
          wheelMode: 'scroll',
          autoScroll: true,
          autoScrollOffset: 20,
          segmentOptions: {
            overlay: true, markers: true, overlayOpacity: 0.25,
            overlayBorderWidth: 2, overlayCornerRadius: 3,
            overlayLabelAlign: 'left', overlayLabelVerticalAlign: 'top',
            overlayLabelPadding: 4, overlayFontSize: 11,
          },
        },
        overview: {
          container: overviewEl,
          waveformColor: '#6366f1',
          playedWaveformColor: '#312e81',
          playheadColor: '#f59e0b',
        },
        zoomLevels: ZOOM_LEVELS,
        webAudio: { audioContext: (audioCtxRef.current = new AudioContext()), scale: 512, multiChannel: false },
        logger: console.error,
        createSegmentMarker: createMarkerFactory(isMobile),
      },
      (err, peaks) => {
        if (err || !peaks) { setFallbackSrc(src); return; }
        peaksRef.current = peaks;
        const dur = peaks.player.getDuration();
        setDuration(dur);
        setZoomSeconds(1);
        peaks.views.getView('zoomview')?.setZoom({ scale: 'auto' });
        peaks.views.getView('zoomview')?.enableSegmentDragging(!readOnly);

        peaks.on('player.timeupdate', (time) => setCurrentTime(time));
        peaks.on('player.playing', () => setPlaying(true));
        peaks.on('player.pause', () => { setPlaying(false); setPlayingSeg(null); });
        peaks.on('player.ended', () => { setPlaying(false); setPlayingSeg(null); });
        peaks.on('segments.dragend', ({ segment, marker }) => {
          const d = peaks.player.getDuration();
          if (marker) {
            if (segment.startTime < 0) segment.update({ startTime: 0 });
            if (segment.endTime > d) segment.update({ endTime: d });
          } else {
            const segDur = segment.endTime - segment.startTime;
            if (segment.endTime > d) segment.update({ startTime: d - segDur, endTime: d });
            else if (segment.startTime < 0) segment.update({ startTime: 0, endTime: segDur });
          }
          syncSegmentsFromPeaks();
        });
        peaks.on('zoomview.update', ({ startTime, endTime }) => {
          visibleWindowRef.current = { start: startTime, end: endTime };
          setZoomSeconds(Math.max(1, dur + 1 - (endTime - startTime)));
        });
        const findShape = (segId) => {
          const layer = peaks.views.getView('zoomview')?._segmentsLayer;
          return layer && Object.values(layer._segmentShapes || {}).find(
            (s) => s.getSegment?.()?.id === segId,
          );
        };
        peaks.on('segments.dragged', ({ segment, startMarker, marker }) => {
          const d = peaks.player.getDuration();
          let clamped = false;
          if (marker) {
            if (segment.startTime < 0) { segment.update({ startTime: 0 }); clamped = true; }
            if (segment.endTime > d) { segment.update({ endTime: d }); clamped = true; }
          } else if (segment.endTime > d) {
            findShape(segment.id)?._overlay?.stopDrag();
            return;
          }
          if (!clamped) {
            const dragTime = startMarker ? segment.startTime : segment.endTime;
            const { start, end } = visibleWindowRef.current;
            const span = end - start;
            const view = peaks.views.getView('zoomview');
            if (dragTime < start + span * 0.1) view?.scrollWaveform({ seconds: -(span * 0.02) });
            else if (dragTime > end - span * 0.1) view?.scrollWaveform({ seconds: span * 0.02 });
          }
        });
        peaks.on('segments.click', ({ segment }) => {
          if (!segment.id) return;
          const next = new Set([segment.id]);
          setSelectedIds(next);
          updatePeaksBorders(peaks, next);
          anchorIdRef.current = segment.id;
          setEditableSegment(segment.id);
        });

        setLoaded(true);
      },
    );
  }

  useEffect(() => {
    if (!audioFileURL) return;
    let cancelled = false;
    destroyPeaks();

    resolveMediaSrc(audioFileURL)
      .then(({ src, isBlob, imageSrc: img }) => {
        if (cancelled) { if (isBlob) URL.revokeObjectURL(src); return; }
        if (isBlob) blobUrlRef.current = src;
        if (img) setImageSrc(img);
        initPeaks(src);
        // Decode audio for sample-accurate segment stop scheduling
        fetch(src)
          .then((r) => r.arrayBuffer())
          .then((ab) => { if (!cancelled && audioCtxRef.current) return audioCtxRef.current.decodeAudioData(ab); })
          .then((buf) => { if (!cancelled && buf) audioBufferRef.current = buf; })
          .catch(() => {});
      })
      .catch((e) => { if (!cancelled) setError(e?.message ?? 'Failed to load audio'); });

    return () => { cancelled = true; destroyPeaks(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioFileURL]);

  useEffect(() => {
    if (!loaded || !initialSegments?.length || initialSegmentsLoadedRef.current) return;
    initialSegmentsLoadedRef.current = true;
    for (const s of initialSegments) {
      addSegmentRegion(s.startTime ?? s.start, s.endTime ?? s.end, s.label ?? DEFAULT_LABEL, s.tag ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, initialSegments]);

  useEffect(() => {
    function onKeyDown(e) {
      if (readOnly) return;
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      const active = document.activeElement;
      if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA')) return;
      if (selectedIds.size === 0) return;
      const p = peaksRef.current;
      for (const id of selectedIds) p?.segments.removeById(id);
      setSegments((prev) => {
        const next = prev.filter((s) => !selectedIds.has(s.id));
        onSegmentsChange?.(next);
        return next;
      });
      setSelectedIds(new Set());
      anchorIdRef.current = null;
      setEditableSegment(null);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readOnly, selectedIds, onSegmentsChange]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = playbackRate;
  }, [playbackRate]);

  function togglePlay() {
    const p = peaksRef.current;
    if (!p) return;
    if (sourceNodeRef.current) {
      cancelAnimationFrame(segPlayRafRef.current);
      try { sourceNodeRef.current.stop(); } catch (_) {}
      sourceNodeRef.current = null;
      setPlayingSeg(null);
      setPlaying(false);
      return;
    }
    if (playing) {
      clearTimeout(segPlayTimerRef.current);
      p.player.pause();
    } else {
      p.player.play();
    }
  }

  function handleZoom(_, val) {
    setZoomSeconds(val);
    peaksRef.current?.views.getView('zoomview')?.setZoom({ seconds: duration + 1 - val });
  }

  function createSegment() {
    const p = peaksRef.current;
    if (!p) return;
    const dur = p.player.getDuration();
    let t = r3(p.player.getCurrentTime());
    const containing = segments.find((s) => t >= s.start && t < s.end);
    if (containing) t = containing.end;
    const nextStart = segments.map((s) => s.start).filter((s) => s > t)
      .reduce((min, s) => (min === null || s < min ? s : min), null);
    const end = r3(Math.min(t + DEFAULT_SEGMENT_DURATION, nextStart != null ? nextStart - 0.001 : dur, dur));
    if (t >= dur || end <= t) return;
    addSegmentRegion(t, end, undefined, undefined, true);
  }

  function deleteSegment(id) {
    peaksRef.current?.segments.removeById(id);
    setSegments((prev) => {
      const next = prev.filter((s) => s.id !== id);
      onSegmentsChange?.(next);
      return next;
    });
    setSelectedIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
    setEditableSegment(null);
  }

  function handleSegmentPlay(id) {
    const p = peaksRef.current;
    if (!p) return;
    cancelAnimationFrame(segPlayRafRef.current);
    clearTimeout(segPlayTimerRef.current);
    try { sourceNodeRef.current?.stop(); sourceNodeRef.current?.disconnect(); } catch (_) {}
    sourceNodeRef.current = null;
    if (playingSegIdRef.current === id) {
      setPlayingSeg(null);
      setPlaying(false);
      return;
    }
    const seg = p.segments.getSegment(id);
    if (!seg) return;
    const buffer = audioBufferRef.current;
    const audioCtx = audioCtxRef.current;
    if (!buffer || !audioCtx || audioCtx.state === 'closed') {
      // Fallback: setTimeout (buffer not yet decoded)
      setPlayingSeg(id);
      p.player.seek(seg.startTime);
      p.player.play();
      const rate = audioRef.current?.playbackRate ?? 1;
      segPlayTimerRef.current = setTimeout(() => {
        if (playingSegIdRef.current !== id) return;
        peaksRef.current?.player.seek(seg.endTime);
        peaksRef.current?.player.pause();
      }, ((seg.endTime - seg.startTime) / rate) * 1000);
      return;
    }
    if (audioCtx.state === 'suspended') audioCtx.resume();
    setPlayingSeg(id);
    setPlaying(true);
    const rate = audioRef.current?.playbackRate ?? 1;
    const source = audioCtx.createBufferSource();
    source.buffer = buffer;
    source.playbackRate.value = rate;
    source.connect(audioCtx.destination);
    sourceNodeRef.current = source;
    const startCtxTime = audioCtx.currentTime;
    const segDuration = (seg.endTime - seg.startTime) / rate;
    source.start(startCtxTime, seg.startTime, segDuration);
    function syncCursor() {
      if (playingSegIdRef.current !== id || !peaksRef.current) return;
      const pos = Math.min(seg.startTime + (audioCtx.currentTime - startCtxTime) * rate, seg.endTime);
      peaksRef.current.player.seek(pos);
      if (pos < seg.endTime) segPlayRafRef.current = requestAnimationFrame(syncCursor);
    }
    segPlayRafRef.current = requestAnimationFrame(syncCursor);
    source.onended = () => {
      if (playingSegIdRef.current !== id) return;
      cancelAnimationFrame(segPlayRafRef.current);
      source.disconnect();
      peaksRef.current?.player.seek(seg.endTime);
      sourceNodeRef.current = null;
      setPlayingSeg(null);
      setPlaying(false);
    };
  }

  function handleRowClick(e, id) {
    const p = peaksRef.current;
    if (e.shiftKey && anchorIdRef.current) {
      const anchorIdx = segments.findIndex((s) => s.id === anchorIdRef.current);
      const clickIdx = segments.findIndex((s) => s.id === id);
      const [lo, hi] = anchorIdx <= clickIdx ? [anchorIdx, clickIdx] : [clickIdx, anchorIdx];
      const next = new Set(segments.slice(lo, hi + 1).map((s) => s.id));
      setSelectedIds(next); updatePeaksBorders(p, next);
    } else if (e.ctrlKey || e.metaKey) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        updatePeaksBorders(p, next);
        return next;
      });
      anchorIdRef.current = id;
    } else {
      const next = new Set([id]);
      setSelectedIds(next); updatePeaksBorders(p, next);
      anchorIdRef.current = id;
      setEditableSegment(id);
    }
  }

  function updateSegmentLabel(id, label) {
    setSegments((prev) => {
      const seg = prev.find((s) => s.id === id);
      const tagColor = getTagColorRef.current(seg?.tag);
      const color = tagColor ?? (LABEL_MAP[label]?.color ?? LABEL_MAP[DEFAULT_LABEL].color);
      peaksRef.current?.segments.getSegment(id)?.update({ color, borderColor: color, labelText: seg?.tag || label });
      const next = prev.map((s) => (s.id === id ? { ...s, label } : s));
      onSegmentsChange?.(next);
      return next;
    });
  }

  function updateSegmentTag(id, tag) {
    setSegments((prev) => {
      const seg = prev.find((s) => s.id === id);
      const tagColor = getTagColorRef.current(tag);
      const color = tagColor ?? (LABEL_MAP[seg?.label ?? DEFAULT_LABEL]?.color ?? LABEL_MAP[DEFAULT_LABEL].color);
      peaksRef.current?.segments.getSegment(id)?.update({ color, borderColor: color, labelText: tag || seg?.label || DEFAULT_LABEL });
      const next = prev.map((s) => (s.id === id ? { ...s, tag } : s));
      onSegmentsChange?.(next);
      return next;
    });
  }

  function commitSegmentField(id, field, raw) {
    const parsed = parseFloat(raw);
    if (isNaN(parsed)) return;
    const seg = segments.find((s) => s.id === id);
    if (!seg) return;
    let value = r3(Math.max(0, Math.min(parsed, duration)));
    if (field === 'start' && value >= seg.end) value = r3(Math.max(0, seg.end - 0.001));
    if (field === 'end' && value <= seg.start) value = r3(Math.min(duration, seg.start + 0.001));
    peaksRef.current?.segments.getSegment(id)
      ?.update(field === 'start' ? { startTime: value } : { endTime: value });
    setSegments((prev) => {
      const next = prev.map((s) => (s.id === id ? { ...s, [field]: value } : s));
      onSegmentsChange?.(next);
      return next;
    });
    setRawValues((prev) => { const next = { ...prev }; delete next[`${id}-${field}`]; return next; });
  }

  const fmt = (s) => {
    const m = Math.floor(s / 60);
    const sec = (s % 60).toFixed(3).padStart(6, '0');
    return `${m}:${sec}`;
  };

  useImperativeHandle(ref, () => ({
    playByTime(startSecs) {
      const p = peaksRef.current;
      if (!p) return;
      const seg = p.segments.getSegments().find((s) => Math.abs(s.startTime - startSecs) < 0.01);
      if (seg) {
        setPlayingSeg(seg.id);
        p.player.playSegment(seg, false);
      } else {
        p.player.seek(startSecs);
        p.player.play();
      }
    },
  }));

  return (
    <Box sx={{ width: '100%', p: 1 }}>
      <audio ref={audioRef} style={{ display: 'none' }} />

      {imageSrc && (
        <Box sx={{ mb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button size="small" variant="outlined" onClick={() => setImageDialogOpen((v) => !v)}
              sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
              {imageDialogOpen ? 'Hide Image' : 'View Image'}
            </Button>
          </Box>
          {imageDialogOpen && (
            <Box sx={{ mt: 1, textAlign: 'center' }}>
              <img src={imageSrc} alt="file preview"
                style={{ maxWidth: '100%', maxHeight: 300, objectFit: 'contain', borderRadius: 4 }} />
            </Box>
          )}
        </Box>
      )}

      <Box ref={zoomviewRef}
        sx={{ width: '100%', height: fallbackSrc ? 0 : 150, background: '#fff', borderRadius: 1, mb: fallbackSrc ? 0 : 1, overflow: 'hidden' }} />
      <Box ref={overviewRef}
        sx={{ width: '100%', height: fallbackSrc ? 0 : 50, background: '#fff', borderRadius: 1, mb: fallbackSrc ? 0 : 1, overflow: 'hidden' }} />

      {!loaded && !error && !fallbackSrc && (
        <Box display="flex" justifyContent="center" py={3}><CircularProgress size={24} /></Box>
      )}

      {fallbackSrc && (
        <Box sx={{ width: '100%', px: 1, pb: 1 }}>
          <audio controls src={fallbackSrc} style={{ width: '100%' }} />
        </Box>
      )}

      {error && (
        <Typography color="error" variant="caption" display="block" textAlign="center" py={1}>
          {error}
        </Typography>
      )}

      {loaded && !fallbackSrc && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
          <IconButton onClick={togglePlay} size="small" color="primary">
            {playing ? <PauseIcon /> : <PlayArrowIcon />}
          </IconButton>
          <Typography variant="caption" sx={{ minWidth: 110, fontVariantNumeric: 'tabular-nums' }}>
            {fmt(currentTime)} / {fmt(duration)}
          </Typography>
          {/* Zoom + speed group: wraps to its own row on mobile (order:1), stays inline on desktop (order:0) */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: { xs: '1 1 100%', md: 1 }, order: { xs: 1, md: 0 } }}>
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>Zoom</Typography>
            <Slider size="small" min={1} max={Math.max(duration, 1)} step={0.5}
              value={zoomSeconds} onChange={handleZoom} sx={{ flex: 1 }} />
            <Autocomplete
              freeSolo
              size="small"
              options={['0.5', '0.75', '1', '1.25', '1.5', '2']}
              value={String(playbackRate)}
              onChange={(_, val) => {
                const n = parseFloat(val);
                if (!isNaN(n) && n >= 0.1 && n <= 4) setPlaybackRate(n);
              }}
              onInputChange={(_, val) => {
                const n = parseFloat(val);
                if (!isNaN(n) && n >= 0.1 && n <= 4) setPlaybackRate(n);
              }}
              disableClearable
              slotProps={{
                popper: { sx: (t) => ({ zIndex: t.zIndex.modal + 200_000 }) },
              }}
              sx={{ width: 80 }}
              renderInput={({ inputProps, ...params }) => (
                <TextField {...params} size="small" label="Speed"
                  slotProps={{ htmlInput: { ...inputProps, style: { fontSize: '0.75rem', padding: '2px 4px' } } }} />
              )}
              getOptionLabel={(o) => `${o}x`}
            />
          </Box>
          {!readOnly && (
            <Tooltip title="Create segment at playhead">
              <Button size="small" variant="contained" startIcon={<AddIcon />}
                onClick={createSegment} sx={{ whiteSpace: 'nowrap', ml: { xs: 'auto', md: 1 } }}>
                Segment
              </Button>
            </Tooltip>
          )}
        </Box>
      )}

      {segments.length > 0 && !hideTable && (
        <Box sx={{ mt: 1, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <Table size="small" sx={{ minWidth: { xs: 480, md: 'auto' } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ py: 0.5, width: 30 }}>#</TableCell>
                <TableCell sx={{ py: 0.5 }}>Start (s)</TableCell>
                <TableCell sx={{ py: 0.5 }}>End (s)</TableCell>
                <TableCell sx={{ py: 0.5 }}>Duration</TableCell>
                {showLabels && <TableCell sx={{ py: 0.5, width: 130 }}>Label</TableCell>}
                {tagOptions && <TableCell sx={{ py: 0.5 }}>Tags</TableCell>}
                <TableCell sx={{ py: 0.5, width: 40 }} />
                {!readOnly && <TableCell sx={{ py: 0.5, width: 40 }} />}
              </TableRow>
            </TableHead>
            <TableBody>
              {segments.map((seg, i) => {
                const selected = selectedIds.has(seg.id);
                return (
                  <TableRow key={seg.id} onClick={(e) => handleRowClick(e, seg.id)}
                    sx={{
                      cursor: 'pointer', userSelect: 'none',
                      bgcolor: selected ? 'rgba(99,102,241,0.12)' : isAutoGenTag(seg.tag) ? 'rgba(245,158,11,0.10)' : 'transparent',
                      '&:hover': { bgcolor: selected ? 'rgba(99,102,241,0.2)' : isAutoGenTag(seg.tag) ? 'rgba(245,158,11,0.18)' : 'action.hover' },
                    }}>
                    <TableCell sx={{ py: 0.5, color: 'text.secondary' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '2px', flexShrink: 0, bgcolor: getTagColorRef.current(seg.tag) ?? (LABEL_MAP[seg.label ?? DEFAULT_LABEL]?.color ?? '#6366f1') }} />
                        {i + 1}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 0.5 }}>
                      <Box>
                        <TextField size="small" inputMode="decimal"
                          value={rawValues[`${seg.id}-start`] ?? String(seg.start)}
                          onChange={(e) => !readOnly && setRawValues((p) => ({ ...p, [`${seg.id}-start`]: e.target.value }))}
                          onFocus={() => {
                            peaksRef.current?.player.seek(seg.start);
                            if (!readOnly) setRawValues((p) => ({ ...p, [`${seg.id}-start`]: String(seg.start) }));
                          }}
                          onBlur={() => !readOnly && commitSegmentField(seg.id, 'start', rawValues[`${seg.id}-start`] ?? '')}
                          onKeyDown={(e) => e.key === 'Enter' && e.target.blur()}
                          onClick={(e) => e.stopPropagation()}
                          inputProps={{ readOnly, style: { fontVariantNumeric: 'tabular-nums', padding: '2px 6px' } }}
                          sx={{ width: { xs: 72, md: 90 } }} />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontVariantNumeric: 'tabular-nums', lineHeight: 1.2, pl: '6px' }}>
                          {fmt(seg.start)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 0.5 }}>
                      <Box>
                        <TextField size="small" inputMode="decimal"
                          value={rawValues[`${seg.id}-end`] ?? String(seg.end)}
                          onChange={(e) => !readOnly && setRawValues((p) => ({ ...p, [`${seg.id}-end`]: e.target.value }))}
                          onFocus={() => {
                            peaksRef.current?.player.seek(seg.end);
                            if (!readOnly) setRawValues((p) => ({ ...p, [`${seg.id}-end`]: String(seg.end) }));
                          }}
                          onBlur={() => !readOnly && commitSegmentField(seg.id, 'end', rawValues[`${seg.id}-end`] ?? '')}
                          onKeyDown={(e) => e.key === 'Enter' && e.target.blur()}
                          onClick={(e) => e.stopPropagation()}
                          inputProps={{ readOnly, style: { fontVariantNumeric: 'tabular-nums', padding: '2px 6px' } }}
                          sx={{ width: { xs: 72, md: 90 } }} />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontVariantNumeric: 'tabular-nums', lineHeight: 1.2, pl: '6px' }}>
                          {fmt(seg.end)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 0.5, fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>
                      {fmt(Math.max(0, seg.end - seg.start))}
                    </TableCell>
                    {showLabels && (
                      <TableCell sx={{ py: 0.5 }} onClick={(e) => e.stopPropagation()}>
                        {readOnly ? (
                          <TextField
                            size="small"
                            variant="outlined"
                            value={LABEL_MAP[seg.label ?? DEFAULT_LABEL]?.label ?? seg.label ?? DEFAULT_LABEL}
                            slotProps={{ input: { readOnly: true } }}
                            sx={(t) => ({
                              width: 120,
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                backgroundColor: t.palette.action.hover,
                                fontSize: '0.75rem',
                                color: LABEL_MAP[seg.label ?? DEFAULT_LABEL]?.color,
                                fontWeight: 600,
                              },
                            })}
                          />
                        ) : (
                          <Select
                            size="small"
                            value={seg.label ?? DEFAULT_LABEL}
                            onChange={(e) => updateSegmentLabel(seg.id, e.target.value)}
                            MenuProps={{
                              disableScrollLock: true,
                              sx: (t) => ({ zIndex: t.zIndex.modal + 200_000 }),
                            }}
                            sx={{
                              width: 120,
                              fontSize: '0.75rem',
                              '& .MuiSelect-select': { py: '2px', pl: '8px' },
                              color: LABEL_MAP[seg.label ?? DEFAULT_LABEL]?.color,
                              fontWeight: 600,
                            }}
                          >
                            {labels.map((l) => (
                              <MenuItem key={l.value} value={l.value} sx={{ fontSize: '0.75rem', color: l.color, fontWeight: 600 }}>
                                {l.label}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      </TableCell>
                    )}
                    {tagOptions && (
                      <TableCell sx={{ py: 0.5, minWidth: 180 }} onClick={(e) => e.stopPropagation()}>
                        {readOnly ? (
                          <TextField
                            size="small"
                            variant="outlined"
                            value={seg.tag ?? '—'}
                            slotProps={{ input: { readOnly: true } }}
                            sx={(t) => ({
                              minWidth: 160,
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                backgroundColor: t.palette.action.hover,
                                fontSize: '0.75rem',
                              },
                            })}
                          />
                        ) : (
                          <Autocomplete
                            size="small"
                            freeSolo
                            options={tagOptions ?? []}
                            value={seg.tag ?? null}
                            onChange={(_, tag) => updateSegmentTag(seg.id, tag)}
                            onInputChange={(_, val, reason) => {
                              if (reason === 'input') updateSegmentTag(seg.id, val || null);
                            }}
                            slotProps={{
                              popper: { sx: (t) => ({ zIndex: t.zIndex.modal + 200_000 }) },
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label={tagOptions.length > 0 ? 'Select or type tag' : 'Type tag'}
                                size="small"
                                variant="outlined"
                                sx={(t) => ({
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    backgroundColor: t.palette.background.paper,
                                    fontSize: '0.75rem',
                                  },
                                })}
                              />
                            )}
                            noOptionsText="Type to add new tag"
                            sx={{ minWidth: 160 }}
                          />
                        )}
                      </TableCell>
                    )}
                    <TableCell sx={{ py: 0.5 }}>
                      <IconButton size="small" color="primary"
                        onClick={(e) => { e.stopPropagation(); handleSegmentPlay(seg.id); }}>
                        {playingSegId === seg.id ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
                      </IconButton>
                    </TableCell>
                    {!readOnly && (
                      <TableCell sx={{ py: 0.5 }}>
                        <IconButton size="small" color="error"
                          onClick={(e) => { e.stopPropagation(); deleteSegment(seg.id); }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
});

export default SegmentationPlayer;
